import type { FC, PropsWithChildren } from 'react';

import { cva } from 'cva';
import {
  Separator as RACSeparator,
  type SeparatorProps,
} from 'react-aria-components/Separator';

import { Chip } from '../chip/chip';
import { Heading } from '../heading/heading';

import styles from './separator.module.css';

type Variant = 'main' | 'sub';
type Orientation = 'vertical' | 'horizontal';

type Props = PropsWithChildren<
  {
    className?: string;
    variant?: Variant;
    orientation?: Orientation;
    label?: string;
  } & SeparatorProps
>;

const separatorVariants = cva({
  base: styles.separator,
  variants: {
    orientation: {
      vertical: styles.vertical,
      horizontal: styles.horizontal,
    },
    variant: {
      main: '',
      sub: '',
    },
  },
  compoundVariants: [
    {
      orientation: 'vertical',
      variant: 'main',
      className: styles.main_vertical,
    },
    { orientation: 'vertical', variant: 'sub', className: styles.sub_vertical },
    {
      orientation: 'horizontal',
      variant: 'main',
      className: styles.main_horizontal,
    },
    {
      orientation: 'horizontal',
      variant: 'sub',
      className: styles.sub_horizontal,
    },
  ],
});

/**
 * Separator consists of a single element that represents the divider.
 *
 * Can be used to separate content in a layout vertically or horizontally with different thickness of line, and with an optional label.
 *
 * ```tsx
 * <Separator variant="sub" />
 * ```
 *
 * ```tsx
 * <Separator variant="sub">Lorem ipsum</Separator>
 * ```
 */
export const Separator: FC<Props> = ({
  className,
  variant = 'main',
  children,
  orientation = 'horizontal',
  ...props
}) => {
  const classes = separatorVariants({ className, variant, orientation });
  const separator = <RACSeparator {...props} className={classes} />;

  if (children) {
    const level = variant === 'main' ? 2 : 3;
    return (
      <Heading level={level} className={styles.separator_container}>
        {separator}
        <Chip variant="pill" color="default" className={styles.chip} truncate>
          {children}
        </Chip>
      </Heading>
    );
  }

  return separator;
};
